import { useState, useEffect, useCallback } from 'react';
import { useEnhancedWebSocket } from './useEnhancedWebSocket';

/**
 * Public Analytics WebSocket Hook
 * Handles real-time public analytics updates (no authentication required)
 */
export const usePublicAnalyticsWebSocket = (options = {}) => {
  const {
    onAnalyticsUpdate,
    onError,
    autoSubscribe = true,
    updateThrottleMs = 5000 // Throttle updates to prevent too frequent re-renders
  } = options;

  const [analyticsData, setAnalyticsData] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [updateCount, setUpdateCount] = useState(0);

  // Throttling state
  const [lastUpdateTime, setLastUpdateTime] = useState(0);
  const [pendingUpdate, setPendingUpdate] = useState(null);

  const {
    connectionState,
    isConnected,
    subscribeToPublicAnalytics,
    unsubscribeFromPublicAnalytics,
    setEventHandler,
    connect,
    disconnect
  } = useEnhancedWebSocket({
    autoConnect: true,
    reconnectAttempts: 5,
    reconnectInterval: 3000
  });

  /**
   * Handle analytics updates from WebSocket with throttling
   */
  const handleAnalyticsUpdate = useCallback((data) => {
    console.log('📈 Public analytics update received:', data);
    
    const now = Date.now();
    
    // If enough time has passed since last update, apply immediately
    if (now - lastUpdateTime >= updateThrottleMs) {
      setAnalyticsData(data);
      setLastUpdate(new Date());
      setUpdateCount(prev => prev + 1);
      setLastUpdateTime(now);
      
      // Call external handler if provided
      onAnalyticsUpdate?.(data);
    } else {
      // Store pending update to apply later
      setPendingUpdate(data);
    }
  }, [onAnalyticsUpdate, updateThrottleMs, lastUpdateTime]);

  /**
   * Handle connection state changes
   */
  const handleConnectionChange = useCallback((state) => {
    console.log('🔌 Public Analytics WebSocket connection state:', state);
    
    // Public analytics doesn't require authentication, so subscribe on any open connection
    if (state === 'OPEN' && autoSubscribe && !isSubscribed) {
      setTimeout(() => {
        subscribe();
      }, 500); // Small delay to ensure connection is stable
    } else if (state === 'CLOSED') {
      setIsSubscribed(false);
    }
  }, [autoSubscribe, isSubscribed]);

  /**
   * Handle WebSocket errors
   */
  const handleError = useCallback((error) => {
    console.error('❌ Public Analytics WebSocket error:', error);
    onError?.(error);
  }, [onError]);

  /**
   * Subscribe to public analytics updates
   */
  const subscribe = useCallback(() => {
    if (isConnected && !isSubscribed) {
      const success = subscribeToPublicAnalytics();
      if (success) {
        setIsSubscribed(true);
        console.log('✅ Subscribed to public analytics updates');
      }
      return success;
    }
    return false;
  }, [isConnected, isSubscribed, subscribeToPublicAnalytics]);

  /**
   * Unsubscribe from public analytics updates
   */
  const unsubscribe = useCallback(() => {
    if (isSubscribed) {
      const success = unsubscribeFromPublicAnalytics();
      if (success) {
        setIsSubscribed(false);
        console.log('❌ Unsubscribed from public analytics updates');
      }
      return success;
    }
    return false;
  }, [isSubscribed, unsubscribeFromPublicAnalytics]);

  /**
   * Manually refresh analytics data
   */
  const refreshAnalytics = useCallback(() => {
    if (isConnected) {
      // Unsubscribe and resubscribe to trigger fresh data
      unsubscribe();
      setTimeout(() => {
        subscribe();
      }, 500);
    }
  }, [isConnected, subscribe, unsubscribe]);

  /**
   * Get specific analytics section
   */
  const getPoolPerformance = useCallback(() => {
    return analyticsData?.poolPerformance || [];
  }, [analyticsData]);

  const getTradingActivity = useCallback(() => {
    return analyticsData?.tradingActivity || [];
  }, [analyticsData]);

  const getLeaderboard = useCallback(() => {
    return analyticsData?.leaderboard || [];
  }, [analyticsData]);

  const getTransparencyMetrics = useCallback(() => {
    return analyticsData?.transparencyMetrics || {};
  }, [analyticsData]);

  /**
   * Get analytics summary
   */
  const getAnalyticsSummary = useCallback(() => {
    if (!analyticsData) return null;

    const poolPerformance = getPoolPerformance();
    const tradingActivity = getTradingActivity();
    const leaderboard = getLeaderboard();
    const transparencyMetrics = getTransparencyMetrics();

    return {
      totalPools: poolPerformance.length,
      totalTrades: tradingActivity.length,
      totalManagers: leaderboard.length,
      totalAUM: transparencyMetrics.totalAssetsUnderManagement || 0,
      totalInvestors: transparencyMetrics.totalInvestors || 0,
      averageReturn: poolPerformance.reduce((sum, pool) => sum + (pool.return30d || 0), 0) / poolPerformance.length || 0,
      topPerformer: leaderboard[0] || null,
      recentTradeCount: tradingActivity.filter(trade => {
        const tradeTime = new Date(trade.timestamp);
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        return tradeTime > oneDayAgo;
      }).length
    };
  }, [analyticsData, getPoolPerformance, getTradingActivity, getLeaderboard, getTransparencyMetrics]);

  // Set up event handlers
  useEffect(() => {
    setEventHandler('onPublicAnalyticsUpdate', handleAnalyticsUpdate);
    setEventHandler('onConnectionChange', handleConnectionChange);
    setEventHandler('onError', handleError);
  }, [setEventHandler, handleAnalyticsUpdate, handleConnectionChange, handleError]);

  // Auto-subscribe when connected
  useEffect(() => {
    if (isConnected && autoSubscribe && !isSubscribed) {
      subscribe();
    }
  }, [isConnected, autoSubscribe, isSubscribed, subscribe]);

  // Handle throttled updates
  useEffect(() => {
    if (pendingUpdate) {
      const timeoutId = setTimeout(() => {
        setAnalyticsData(pendingUpdate);
        setLastUpdate(new Date());
        setUpdateCount(prev => prev + 1);
        setLastUpdateTime(Date.now());
        setPendingUpdate(null);
        
        // Call external handler if provided
        onAnalyticsUpdate?.(pendingUpdate);
      }, updateThrottleMs - (Date.now() - lastUpdateTime));

      return () => clearTimeout(timeoutId);
    }
  }, [pendingUpdate, updateThrottleMs, lastUpdateTime, onAnalyticsUpdate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isSubscribed) {
        unsubscribe();
      }
    };
  }, [isSubscribed, unsubscribe]);

  return {
    // Analytics data
    analyticsData,
    lastUpdate,
    updateCount,
    
    // Connection state
    isConnected,
    isSubscribed,
    connectionState,
    
    // Control methods
    subscribe,
    unsubscribe,
    refreshAnalytics,
    connect,
    disconnect,
    
    // Data accessors
    getPoolPerformance,
    getTradingActivity,
    getLeaderboard,
    getTransparencyMetrics,
    getAnalyticsSummary,
    
    // Utility methods
    hasData: analyticsData !== null,
    isLoading: isConnected && !analyticsData && isSubscribed,
    
    // Individual data sections
    poolPerformance: getPoolPerformance(),
    tradingActivity: getTradingActivity(),
    leaderboard: getLeaderboard(),
    transparencyMetrics: getTransparencyMetrics(),
    summary: getAnalyticsSummary(),
    
    // Update status
    hasPendingUpdate: pendingUpdate !== null,
    timeSinceLastUpdate: lastUpdate ? Date.now() - lastUpdate.getTime() : null,
    
    // Data freshness indicators
    isDataFresh: lastUpdate && (Date.now() - lastUpdate.getTime()) < 5 * 60 * 1000, // Fresh if updated within 5 minutes
    isDataStale: lastUpdate && (Date.now() - lastUpdate.getTime()) > 15 * 60 * 1000, // Stale if older than 15 minutes
    
    // Performance metrics
    updatesPerMinute: updateCount > 0 && lastUpdate ? 
      (updateCount / ((Date.now() - lastUpdate.getTime()) / 60000)) : 0
  };
};

export default usePublicAnalyticsWebSocket;